import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Button,
  InlineStack,
  Box,
  Divider,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SummaryCards from "../../Components/Analytics/SummaryCards";
import FilterBar from "../../Components/Analytics/FilterBar";
import analyticsApi from "../../Services/analyticsApi";

const COLORS = ["#5C6AC4", "#47C1BF", "#F49342", "#DC3545", "#00A6A0", "#9C6ADE"];

const Overview = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    include_manual: false,
  });
  const [summaryData, setSummaryData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [productData, setProductData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [summaryResponse, revenueResponse, customersResponse, productsResponse] =
        await Promise.all([
          analyticsApi.getSummary(filters),
          analyticsApi.getRevenueSummary(filters),
          analyticsApi.getCustomerLTV(filters),
          analyticsApi.getBestSellingCourses({ ...filters, limit: 5 }),
        ]);

      setSummaryData(summaryResponse.data.data);
      setRevenueData(revenueResponse.data.data);
      setCustomerData(customersResponse.data.data);
      setProductData(productsResponse.data.data);
    } catch (error) {
      console.error("Error fetching overview data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Prepare revenue trend data (last 7 days)
  const revenueTrendData = revenueData?.trend?.slice(-7).map((item) => ({
    date: item.date,
    revenue: item.revenue,
  })) || [];

  // Prepare product breakdown data
  const productBreakdownData = revenueData
    ? [
        {
          name: "Courses",
          value: revenueData.byProduct.courses.revenue,
          count: revenueData.byProduct.courses.count,
        },
        {
          name: "Lectures",
          value: revenueData.byProduct.lectures.revenue,
          count: revenueData.byProduct.lectures.count,
        },
        {
          name: "Notes",
          value: revenueData.byProduct.notes.revenue,
          count: revenueData.byProduct.notes.count,
        },
      ]
    : [];

  // Prepare top customers data
  const topCustomersData = customerData?.top_customers?.slice(0, 5).map((customer) => ({
    name: customer.name || "N/A",
    ltv: customer.lifetime_value,
  })) || [];

  // Prepare top products data
  const topProductsData = productData?.courses?.slice(0, 5).map((course) => ({
    name: course.name_en || course.name_ar || "N/A",
    sales: course.sales_count,
    revenue: course.total_revenue,
  })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>
            {payload[0].payload.name || payload[0].payload.date}
          </p>
          {payload.map((item, index) => (
            <p key={index} style={{ margin: "5px 0 0 0", color: item.color }}>
              {item.name}: {typeof item.value === "number" ? item.value.toFixed(2) : item.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Page
      title="Analytics Overview"
      subtitle="Comprehensive view of your platform's performance"
    >
      <BlockStack gap="500">
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          isLoading={isLoading}
        />

        <SummaryCards data={summaryData} isLoading={isLoading} />

        <Layout>
          <Layout.Section variant="oneHalf">
            <Card>
              <div style={{ padding: "16px" }}>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">
                      Revenue Trend (Last 7 Days)
                    </Text>
                    <Button onClick={() => navigate("/admin/analytics/revenue")}>
                      View Details
                    </Button>
                  </InlineStack>
                  <Divider />
                  {revenueTrendData.length > 0 ? (
                    <div style={{ width: "100%", height: "300px" }}>
                      <ResponsiveContainer>
                        <LineChart data={revenueTrendData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            style={{ fontSize: "12px" }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis style={{ fontSize: "12px" }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="revenue"
                            stroke={COLORS[0]}
                            strokeWidth={2}
                            dot={{ fill: COLORS[0] }}
                            name="Revenue (KWD)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Text tone="subdued">No revenue data available</Text>
                  )}
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>

          <Layout.Section variant="oneHalf">
            <Card>
              <div style={{ padding: "16px" }}>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">
                      Revenue by Product Type
                    </Text>
                    <Button onClick={() => navigate("/admin/analytics/revenue")}>
                      View Details
                    </Button>
                  </InlineStack>
                  <Divider />
                  {productBreakdownData.length > 0 ? (
                    <div style={{ width: "100%", height: "300px" }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={productBreakdownData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry) =>
                              `${entry.name}: ${entry.value.toFixed(0)} KWD`
                            }
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {productBreakdownData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Text tone="subdued">No product breakdown available</Text>
                  )}
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>
        </Layout>

        <Layout>
          <Layout.Section variant="oneHalf">
            <Card>
              <div style={{ padding: "16px" }}>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">
                      Top 5 Customers by LTV
                    </Text>
                    <Button onClick={() => navigate("/admin/analytics/customers")}>
                      View Details
                    </Button>
                  </InlineStack>
                  <Divider />
                  {topCustomersData.length > 0 ? (
                    <div style={{ width: "100%", height: "300px" }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={topCustomersData}
                          layout="vertical"
                          margin={{ left: 80 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" style={{ fontSize: "12px" }} />
                          <YAxis
                            dataKey="name"
                            type="category"
                            width={80}
                            style={{ fontSize: "11px" }}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="ltv" fill={COLORS[1]} name="Lifetime Value (KWD)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Text tone="subdued">No customer data available</Text>
                  )}
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>

          <Layout.Section variant="oneHalf">
            <Card>
              <div style={{ padding: "16px" }}>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">
                      Top 5 Best Selling Courses
                    </Text>
                    <Button onClick={() => navigate("/admin/analytics/products")}>
                      View Details
                    </Button>
                  </InlineStack>
                  <Divider />
                  {topProductsData.length > 0 ? (
                    <div style={{ width: "100%", height: "300px" }}>
                      <ResponsiveContainer>
                        <BarChart data={topProductsData} margin={{ bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            style={{ fontSize: "10px" }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis style={{ fontSize: "12px" }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="sales" fill={COLORS[2]} name="Sales Count" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Text tone="subdued">No product data available</Text>
                  )}
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>
        </Layout>

        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Quick Actions
              </Text>
              <Divider />
              <InlineStack gap="300" wrap>
                <Button
                  size="large"
                  onClick={() => navigate("/admin/analytics/revenue")}
                >
                  View Full Revenue Analytics
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate("/admin/analytics/customers")}
                >
                  View Customer Analytics
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate("/admin/analytics/products")}
                >
                  View Product Performance
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate("/admin/analytics/sales")}
                >
                  View All Sales
                </Button>
              </InlineStack>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
};

export default Overview;
